<template>
  <div :id="id" class="w-full"/>
</template>

<script setup>

import Highcharts from "@/highcharts"
import colors from "@/colors"
import { currencyShort } from "@/support"
import { onMounted } from "vue"

const props = defineProps({
  id: { type: String, required: true },
  school: { type: Object, required: true },
})

onMounted(() => {
  const hasDiscounts = props.school.offers_discount
  let tuitionLabel = "Tuition"
  let discountedTuitionLabel = "Resident tuition & fees"

  if (props.school.lds) {
    tuitionLabel = "Non-LDS tuition & fees"
    discountedTuitionLabel = "LDS tuition & fees"
  } else if (props.school.type == "public") {
    tuitionLabel = "Non-resident tuition & fees"
    discountedTuitionLabel = "Resident tuition & fees"
  }

  const pricesArray = Object.values(props.school.recent_prices)
  const yearStart = pricesArray[0].academic_year
  const yearEnd = pricesArray[pricesArray.length - 1].academic_year
  const tuitionSeries = []
  const discountedTuitionSeries = []
  const missingTuitionSeries = []
  const missingDiscountedTuitionSeries = []

  let missingYearStart
  let missingYearEnd

  for (let year = yearStart; year <= yearEnd; year++) {
    const price = props.school.recent_prices[year]
    const tuition = { x: year }

    if (price) {
      tuition.y = price.tuition_FT

      if (missingYearStart) {
        missingYearEnd = missingYearEnd || year
      }
    } else {
      missingYearStart = missingYearStart || year
    }
    tuitionSeries.push(tuition)

    if (hasDiscounts) {
      const discountedTuition = { x: year }

      if (price) {
        discountedTuition.y = price.resident_tuition_FT
      }
      discountedTuitionSeries.push(discountedTuition)
    }
  }

  if (missingYearStart) {
    missingTuitionSeries.push({
      x: missingYearStart - 1,
      y: props.school.recent_prices[missingYearStart - 1].tuition_FT,
    })
    missingTuitionSeries.push({
      x: missingYearEnd,
      y: props.school.recent_prices[missingYearEnd].tuition_FT,
    })
    missingDiscountedTuitionSeries.push({
      x: missingYearStart - 1,
      y: props.school.recent_prices[missingYearStart - 1].resident_tuition_FT,
    })
    missingDiscountedTuitionSeries.push({
      x: missingYearEnd,
      y: props.school.recent_prices[missingYearEnd].resident_tuition_FT,
    })
  }

  const dataLabels = {
    enabled: true,
    formatter() {
      const prev = this.series.points[this.point.index - 1]?.y ||
        this.series.points[this.point.index - 3]?.y

      return tuitionChange(prev, this.point.y)
    },
  }

  const series = [
    {
      name: tuitionLabel,
      data: tuitionSeries,
      zIndex: 2,
      dataLabels,
    },
  ]

  if (hasDiscounts) {
    series.push({
      name: "Missing data",
      dashStyle: "Dash",
      color: colors.gray[300],
      data: missingDiscountedTuitionSeries,
      marker: false,
      enableMouseTracking: false,
      showInLegend: false,
    })
    series.push({
      name: discountedTuitionLabel,
      data: discountedTuitionSeries,
      zIndex: 2,
      dataLabels,
    })
  }

  series.push({
    name: "Missing data",
    dashStyle: "Dash",
    color: colors.gray[300],
    data: missingTuitionSeries,
    marker: false,
    enableMouseTracking: false,
  })

  const chartData = {
    chart: {
      type: "spline",
      height: 300,
    },
    credits: { enabled: false },
    legend: {
      enabled: true,
    },
    title: {
      text: "",
    },
    yAxis: {
      title: {
        text: "",
      },
      labels: {
        formatter() {
          return currencyShort(this.value)
        },
      },
    },
    xAxis: {
      categories: [],
    },
    tooltip: {
      crosshairs: true,
      shared: true,
      headerFormat: '<span style="font-size: 16px;font-weight:600;">{point.key}</span><br/>',
      pointFormat: '<div style="font-size:14px;"><span style="color:{point.color}">●</span> {series.name}: ${point.y}</div><br/>',
    },
    plotOptions: {
      series: {
        events: {
          legendItemClick() {
            return false
          },
        },
      },
      spline: {
        marker: {
          radius: 2,
          lineColor: "#666666",
          lineWidth: 1,
        },
      },
    },
    series,
  }

  new Highcharts.chart(props.id, chartData)
})

const tuitionChange = (prev, current) => {
  if (!prev) {
    return null
  }

  const diff = (((current - prev) / prev) * 100).toFixed(1)

  if (diff >= 0) {
    return `<span style="color:${colors.green[600]}">+${diff}%</span>`
  }

  return `<span style="color:${colors.red[500]}">${diff}%</span>`
}
</script>
